/**
 * Enumerations for customers and enrollment (milestone M4). Values follow the reference;
 * see docs/product-inventory.md appendix parts 7 to 9.
 */

import type { PlanType } from './enums';

/* -------------------------------- Customers ------------------------------- */

export const CUSTOMER_STATUSES = ['Dispatched', 'Active', 'Suspended', 'Inactive'] as const;
export type CustomerStatus = (typeof CUSTOMER_STATUSES)[number];

/**
 * "Connection Type" on Update Account Status and "Type of Change" on line changes:
 * Electronically = also send the change to the carrier; Non-Electronically = record it here only.
 */
export const CONNECTION_TYPES = ['Electronically', 'Non-Electronically'] as const;
export type ConnectionType = (typeof CONNECTION_TYPES)[number];

/**
 * Status changes offered on "Update Account Status", with the permission each needs.
 * Seen in the reference: Dispatched to Inactive (PC637) and "Activate Customer" (PC500).
 * The transitions from Active, Suspended and Inactive are inferred from the permission
 * names Suspend (PC636), Inactive (PC637), Restore (PC638) and Reconnect (PC639).
 */
export const STATUS_TRANSITIONS: Record<
  CustomerStatus,
  Array<{
    to: CustomerStatus;
    code: string;
    label: string;
    reason: 'disconnectReason' | 'suspendReason' | null;
  }>
> = {
  Dispatched: [
    { to: 'Active', code: 'PC500', label: 'Activate Customer', reason: null },
    { to: 'Inactive', code: 'PC637', label: 'Inactive', reason: 'disconnectReason' },
  ],
  Active: [
    { to: 'Suspended', code: 'PC636', label: 'Suspend', reason: 'suspendReason' },
    { to: 'Inactive', code: 'PC637', label: 'Inactive', reason: 'disconnectReason' },
  ],
  Suspended: [
    { to: 'Active', code: 'PC638', label: 'Restore', reason: null },
    { to: 'Inactive', code: 'PC637', label: 'Inactive', reason: 'disconnectReason' },
  ],
  Inactive: [{ to: 'Active', code: 'PC639', label: 'Reconnect', reason: null }],
};

export const CUSTOMER_LANGUAGES = ['English', 'Spanish'] as const;

/** Mailing address choice on enrollment step 2. */
export const MAILING_TYPES = ['same', 'different', 'pobox'] as const;
export type MailingType = (typeof MAILING_TYPES)[number];
export const MAILING_TYPE_LABEL: Record<MailingType, string> = {
  same: 'Same as service address',
  different: 'Different from service address',
  pobox: 'My mailing address is a PO BOX',
};

/**
 * Priority of a customer note: the reference's five levels, in the order of its Add New Note
 * select. Its filter (Display Notes) lists them lowest first; see `NOTE_FILTER_PRIORITIES`.
 */
export const NOTE_PRIORITIES = ['Highest', 'High', 'Medium', 'Low', 'Lowest'] as const;
/** Assumption: the reference's default was not seen; Medium is the middle of its five levels. */
export const DEFAULT_NOTE_PRIORITY: (typeof NOTE_PRIORITIES)[number] = 'Medium';
/** The Display Notes filter lists the levels lowest first. */
export const NOTE_FILTER_PRIORITIES = [...NOTE_PRIORITIES].reverse();

/** Reasons offered on ESN/SIM Change are the Active change reasons for process CHANGE ESN or BOTH. */
export const SIM_CHANGE_PROCESS_TYPES = ['CHANGE ESN', 'BOTH'] as const;

/* ------------------------------- Enrollment ------------------------------- */

export const ENROLLMENT_STATUSES = ['Incomplete', 'Complete'] as const;
export type EnrollmentStatus = (typeof ENROLLMENT_STATUSES)[number];

/** Where an order came from. `BSS` is this application (the reference shows its own name here). */
export const ENROLLMENT_SOURCES = ['BSS', 'WEBSITE', 'API'] as const;
export type EnrollmentSource = (typeof ENROLLMENT_SOURCES)[number];

/**
 * Wizard steps. 1 ZIP, 2 personal information, 3 handover were read in the reference.
 * 4 payment was not seen and is built from the reference's settings and permissions.
 */
export const ENROLLMENT_STEPS = [1, 2, 3, 4] as const;
export const ENROLLMENT_STEP_LABEL: Record<number, string> = {
  1: 'Zip code',
  2: 'Personal information',
  3: 'Handover',
  4: 'Payment',
};

/**
 * Handover types of enrollment step 3, with the reference's labels and permission codes (walk of
 * 2026-10-15). Port In and BYOD were added on 2026-10-21 behind release switches.
 */
export const HANDOVER_TYPES = [
  {
    key: 'sim-in-hand',
    label: 'Customer has a SIM and can give its information',
    code: 'PC251',
    built: true,
  },
  {
    key: 'port-in',
    label: 'Port In- Customer has a SIM and can give its information.',
    code: 'PC251',
    built: true,
  },
  { key: 'shipment', label: 'Shipment- customer does not have a SIM', code: 'PC252', built: true },
  // The reference's own spelling.
  { key: 'byod', label: 'BYOD- Sprint/Verzion', code: 'PC250', built: true },
] as const;
export type HandoverKey = (typeof HANDOVER_TYPES)[number]['key'];
export const BUILT_HANDOVER_KEYS = ['sim-in-hand', 'port-in', 'shipment', 'byod'] as const;

/** "Current Wireless Carrier" of the Port In handover, as the reference lists them. */
export const PORT_CURRENT_CARRIERS = [
  'AT&T',
  'MetroPCS',
  'T-Mobile',
  'Sprint',
  'Verizon',
  'Other',
] as const;
/** "Brand Code" and "SKU Type" of the BYOD handover, as the reference lists them. */
export const BYOD_BRAND_CODES = [
  'Assurance Wireless',
  'Boost',
  'Sprint Prepaid',
  'Virgin Mobile US',
  'Sprint Retail Postpaid',
  'Wholesale',
] as const;
export const BYOD_SKU_TYPES = ['International', 'Bring Your Own', 'New device to Sprint'] as const;

/**
 * Payment at the end of enrollment. `option` is the radio's wording on the reference's step 4.
 * Credit Card (PC319), IVR card payment, PIN and PayPal need a payment gateway and are not built.
 * Dealer wallet has no System Config switch on the reference that was seen (`setting: null`).
 */
export const PAYMENT_METHODS = [
  { key: 'cash', label: 'Cash', option: 'Agent cash', code: 'PC318', setting: 'CASH_PAYMENT' },
  {
    key: 'skip',
    label: 'Skip Payment',
    option: 'Skip payment',
    code: 'PC320',
    setting: 'PREPAID_SKIP_PAYMENT',
  },
  {
    key: 'dealerWallet',
    label: 'Dealer Wallet',
    option: 'Dealer wallet',
    code: 'PC544',
    setting: null,
  },
  // Through the company's payment gateway (switch card-payments): "Credit Card Payment Handover".
  {
    key: 'card',
    label: 'Credit Card',
    option: 'Credit card',
    code: 'PC319',
    setting: null,
  },
] as const;
export type PaymentMethodKey = (typeof PAYMENT_METHODS)[number]['key'];

/** Highest "Advance Billing Month" the reference offers. */
export const MAX_ADVANCE_MONTHS = 24;

/* ------------------------------ Service area ------------------------------ */

export const TRIBAL_OPTIONS = ['No', 'Yes', 'Split'] as const;
export type TribalOption = (typeof TRIBAL_OPTIONS)[number];

/** Programmes a ZIP can be opened for. Prepaid and postpaid enrollment uses `nonLifeline`. */
export const SERVICE_PROGRAMS = [
  ['lifeline', 'Lifeline'],
  ['nonLifeline', 'Non-Lifeline'],
  ['ebb', 'EBB'],
] as const;
export type ServiceProgram = (typeof SERVICE_PROGRAMS)[number][0];

/**
 * First three digits of a ZIP code to state. Used to derive the state when a ZIP is added
 * without one. Approximate: a handful of prefixes straddle state lines, and military and
 * territory prefixes are left out. A state given explicitly always wins.
 */
const ZIP_PREFIX_RANGES: Array<[from: number, to: number, state: string]> = [
  [5, 5, 'NY'],
  [10, 27, 'MA'],
  [28, 29, 'RI'],
  [30, 38, 'NH'],
  [39, 49, 'ME'],
  [50, 54, 'VT'],
  [55, 55, 'MA'],
  [56, 59, 'VT'],
  [60, 69, 'CT'],
  [70, 89, 'NJ'],
  [100, 149, 'NY'],
  [150, 196, 'PA'],
  [197, 199, 'DE'],
  [200, 200, 'DC'],
  [201, 201, 'VA'],
  [202, 205, 'DC'],
  [206, 219, 'MD'],
  [220, 246, 'VA'],
  [247, 268, 'WV'],
  [270, 289, 'NC'],
  [290, 299, 'SC'],
  [300, 319, 'GA'],
  [320, 339, 'FL'],
  [341, 349, 'FL'],
  [350, 369, 'AL'],
  [370, 385, 'TN'],
  [386, 397, 'MS'],
  [398, 399, 'GA'],
  [400, 427, 'KY'],
  [430, 459, 'OH'],
  [460, 479, 'IN'],
  [480, 499, 'MI'],
  [500, 528, 'IA'],
  [530, 549, 'WI'],
  [550, 567, 'MN'],
  [570, 577, 'SD'],
  [580, 588, 'ND'],
  [590, 599, 'MT'],
  [600, 629, 'IL'],
  [630, 658, 'MO'],
  [660, 679, 'KS'],
  [680, 693, 'NE'],
  [700, 714, 'LA'],
  [716, 729, 'AR'],
  [730, 732, 'OK'],
  [733, 733, 'TX'],
  [734, 749, 'OK'],
  [750, 799, 'TX'],
  [800, 816, 'CO'],
  [820, 831, 'WY'],
  [832, 838, 'ID'],
  [840, 847, 'UT'],
  [850, 865, 'AZ'],
  [870, 884, 'NM'],
  [885, 885, 'TX'],
  [889, 898, 'NV'],
  [900, 961, 'CA'],
  [967, 968, 'HI'],
  [970, 979, 'OR'],
  [980, 994, 'WA'],
  [995, 999, 'AK'],
];

/** State a 5-digit ZIP belongs to, or undefined when the prefix is not a US state. */
export function stateForZip(zip: string): string | undefined {
  if (!/^\d{5}$/.test(zip)) return undefined;
  const prefix = Number(zip.slice(0, 3));
  return ZIP_PREFIX_RANGES.find(([from, to]) => prefix >= from && prefix <= to)?.[2];
}

/* ---------------------- Change Account Type (PC648) ----------------------- */

/** "Enrollment Type" of the reference's "CSR Screen For Change Account Type". */
export const SUBSIDY_ENROLLMENT_TYPES = ['ACP', 'LIFELINE'] as const;
export type SubsidyEnrollmentType = (typeof SUBSIDY_ENROLLMENT_TYPES)[number];

/**
 * Plan types offered for each enrollment type. Assumption: the reference fills the Plan select
 * "for the type" without saying how; a combo plan is offered under both.
 */
export const SUBSIDY_PLAN_TYPES: Record<SubsidyEnrollmentType, readonly PlanType[]> = {
  ACP: ['ACP Plan', 'Combo Plan (Lifeline + ACP)'],
  LIFELINE: ['LifeLine', 'Combo Plan (Lifeline + ACP)'],
};

/** "Address Type" of the same screen. */
export const SUBSIDY_ADDRESS_TYPES = ['Permanent', 'Temporary'] as const;

/** "Eligible Program" checkboxes, in the reference's order and words. */
export const ELIGIBLE_PROGRAMS = [
  'Federal Public Housing Assistance',
  'Supplemental Nutrition Assistance Program (SNAP) (f/k/a Food Stamps)',
  'Medicaid',
  'Supplemental Security Income (SSI)',
  'Bureau of Indian Affairs General Assistance',
  'Head Start programs (income qualifying eligibility provision only)',
  'Tribally Administered Temporary Assistance for Needy Families',
  'Food Distribution Program in Indian Reservations (FDIR)',
  'Veterans Pension and Survivors Benefit Program',
  'Substantial Loss of Income',
  'Special Supplemental Nutrition Program for Women, Infants, and Children (WIC)',
] as const;

/** "NLAD Exception Code", shown after "Click to Bypass NLAD". */
export const NLAD_EXCEPTION_CODES = [
  'Improper Transfer',
  'Operations Ceased',
  'Rules Violation',
  'Moved Outside Service Area',
] as const;
